import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  SocketConnectionStage,
  ConnectionReasons,
} from 'src/app/pages/content-generation-canvas/canvas.model';
export interface SocketEvent<T = unknown> {
  type:
    | typeof SocketConnectionStage.CONNECT
    | typeof SocketConnectionStage.MESSAGE
    | typeof SocketConnectionStage.DISCONNECT
    | typeof SocketConnectionStage.ERROR;
  data: { reason: ConnectionReasons; payload: T };
}

@Injectable()
export class SocketBaseHandlerService<T = unknown> {
  private _webSocket_: WebSocket | null = null;
  private readonly _eventStream$ = new Subject<SocketEvent<T>>();
  private _reconnectAttempts = 0;
  private readonly _maxReconnectAttempts = 10;
  private readonly _reconnectDelayMs = 10000;
  private _isManuallyClosed = false;
  private _currentUrl: string | null = null;
  private _reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  connect(url: string): void {
    if (!url) return;
    if (
      this._webSocket_ &&
      (this._webSocket_.readyState === WebSocket.OPEN ||
        this._webSocket_.readyState === WebSocket.CONNECTING)
    )
      return;

    this._currentUrl = url;
    this._isManuallyClosed = false;
    this._webSocket_ = new WebSocket(url);

    this._webSocket_.onopen = () => {
      this._reconnectAttempts = 0;
      this.emitEvent(SocketConnectionStage.CONNECT, {
        reason: ConnectionReasons.SUCCESS,
        payload: null as unknown as T,
      });
    };

    this._webSocket_.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as T;
        this.emitEvent(SocketConnectionStage.MESSAGE, {
          reason: ConnectionReasons.SUCCESS,
          payload: data,
        });
      } catch {
        this.emitEvent(SocketConnectionStage.ERROR, {
          reason: ConnectionReasons.INVALID_MESSAGE,
          payload: null as unknown as T,
        });
      }
    };

    this._webSocket_.onerror = () => {
      this.emitEvent(SocketConnectionStage.ERROR, {
        reason: ConnectionReasons.ERROR,
        payload: null as unknown as T,
      });
    };

    this._webSocket_.onclose = () => {
      console.info(
        'NotificationService',
        'WebSocket connection closed unexpectedly',
        this
      );
      if (!this._isManuallyClosed) {
        if (this._reconnectAttempts < this._maxReconnectAttempts) {
          this.scheduleReconnect();
        } else {
          this.emitEvent(SocketConnectionStage.DISCONNECT, {
            reason: ConnectionReasons.RETRIES_EXCEEDED,
            payload: null as unknown as T,
          });
        }
      } else {
        this.emitEvent(SocketConnectionStage.DISCONNECT, {
          reason: ConnectionReasons.MANUAL,
          payload: null as unknown as T,
        });
      }
    };
  }

  private scheduleReconnect(): void {
    if (this._reconnectAttempts >= this._maxReconnectAttempts) return;
    this._reconnectTimeoutId = setTimeout(() => {
      this._reconnectAttempts++;
      if (this._currentUrl) this.connect(this._currentUrl);
    }, this._reconnectDelayMs);
  }

  disconnect(): void {
    this._isManuallyClosed = true;
    if (this._reconnectTimeoutId) {
      clearTimeout(this._reconnectTimeoutId);
      this._reconnectTimeoutId = null;
    }
    if (this._webSocket_ && this._webSocket_.readyState === WebSocket.OPEN) {
      this._webSocket_.close();
    } else {
      this.emitEvent(SocketConnectionStage.DISCONNECT, {
        reason: ConnectionReasons.MANUAL,
        payload: null as unknown as T,
      });
    }

    this.clearConnection();
  }

  private clearConnection(): void {
    if (this._webSocket_) {
      this._webSocket_.onopen = null;
      this._webSocket_.onmessage = null;
      this._webSocket_.onerror = null;
      this._webSocket_.onclose = null;
      this._webSocket_ = null;
    }
  }

  isSocketCreated(): boolean {
    return this._webSocket_ !== null;
  }

  private emitEvent(
    type: SocketEvent['type'],
    data: { reason: ConnectionReasons; payload: T }
  ): void {
    this._eventStream$.next({ type, data });
  }

  get eventStream(): Observable<SocketEvent<T>> {
    return this._eventStream$.asObservable();
  }
}
